import { classNames } from '@/utils';
import { ChatMessage } from '../../types/agentChat';
import { AgentIcon } from './AgentIcon';
import { ErrorMessage } from './ErrorMessage';
import { MarkdownRenderer } from './MarkdownRenderer';
import { ToolCallsRenderer } from './ToolCallsRenderer';

interface AgentMessageProps {
  message: ChatMessage;
  isFirstInGroup: boolean;
  onRetry?: (errorType: 'error' | 'limitReached') => void;
  showStreamingIndicator?: boolean;
}

export function AgentMessage({
  message,
  isFirstInGroup,
  onRetry,
  showStreamingIndicator = false,
}: AgentMessageProps) {
  return (
    <div className="flex w-full items-start gap-2 justify-self-start pr-2 pb-1 leading-1.5 group-last:pb-0">
      {/* Show icon only for first agent message in a group */}
      {isFirstInGroup ? (
        <AgentIcon className="h-6 w-6 shrink-0" />
      ) : (
        // Invisible spacer to maintain alignment for subsequent messages
        <div className="h-6 w-6 shrink-0" />
      )}

      <div
        className={classNames(
          'min-w-0 flex-1 space-y-2',
          !isFirstInGroup && 'mt-2',
        )}
      >
        {/* Render tool calls ABOVE the message content */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <ToolCallsRenderer toolCalls={message.toolCalls} />
        )}

        {message.type === 'error' ? (
          <ErrorMessage message={message} onRetry={onRetry} />
        ) : (
          message.content && (
            <MarkdownRenderer
              content={message.content}
              className="text-sm leading-relaxed text-gray-700"
            />
          )
        )}

        {showStreamingIndicator && (
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-blue-500"></div>
            <span className="text-sm text-gray-500 italic">Working...</span>
          </div>
        )}
      </div>
    </div>
  );
}
